import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Badge,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  ArchivedHistoryItem,
  getDateString,
  getDateTimeString,
  PopulatedAssessment,
} from '@hb/shared'
import { PropsWithChildren, useMemo, useRef, useState } from 'react'

import { RepeatClockIcon } from '@chakra-ui/icons'
import { useApp } from '@hb/react'
import { useProfile } from '../../../../../contexts/user/ProfileContext'
import archiveIcon from '../../../../../icons/archive.svg'
import { SvgIcon } from '../../../../../icons/SvgIcon'
import unarchiveIcon from '../../../../../icons/unarchive.svg'
import { HistoryNote } from '../../../../Assessments/HistoryNote'
import { UserBadge } from '../../../UserBadge'
import { useAssessmentArchived } from './hooks'
const ConfirmArchiveAlert = ({
  confirmActive,
  onCancel,
  onConfirm,
  actionLoading,
  isArchived,
}: {
  confirmActive: boolean
  onCancel: () => void
  onConfirm: () => void
  actionLoading?: boolean
  isArchived: boolean
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  return (
    <AlertDialog
      size="sm"
      isCentered
      isOpen={confirmActive}
      leastDestructiveRef={cancelRef}
      onClose={() => onCancel()}>
      <AlertDialogOverlay>
        <AlertDialogContent bg="gray.50" gap={0}>
          <AlertDialogBody px={4} py={2}>
            <VStack align="flex-start" py={1}>
              <Text fontFamily="Hero-New" fontSize="lg">
                {isArchived ? 'Unarchive' : 'Archive'} Pregnancy?
              </Text>
              <HStack justify="flex-end" w="100%">
                <Button size="sm" ref={cancelRef} onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  isDisabled={actionLoading}
                  isLoading={actionLoading}
                  color="red.100"
                  colorScheme="red"
                  onClick={onConfirm}>
                  {isArchived ? 'Unarchive' : 'Archive'}
                </Button>
              </HStack>
            </VStack>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

const PregnancyArchiveHistoryItem = ({ item }: { item: ArchivedHistoryItem }) => {
  const { by, group, on, archived, notes } = item
  return (
    <VStack w="100%" p={2} spacing={1} align="flex-start">
      <Flex w="100%" align="center" gap={0.5}>
        {archived ? (
          <SvgIcon filter="hue-rotate(180deg)" src={archiveIcon} />
        ) : (
          <SvgIcon filter="grayscale(100%)" src={unarchiveIcon} />
        )}
        <Text
          position="relative"
          fontSize="xs"
          fontWeight={500}
          color={archived ? 'red.600' : 'gray.600'}
          fontFamily="Hero-New">
          {archived ? 'ARCHIVED' : 'UNARCHIVED'}
        </Text>
        <Flex ml="auto" align="center" gap={1}>
          <Text fontSize="xs" fontWeight={600} color="gray.600">
            {getDateTimeString(on, 'short')}
          </Text>
          <UserBadge userId={by} userGroup={group} />
        </Flex>
      </Flex>
      {notes ? <HistoryNote>{notes}</HistoryNote> : null}
    </VStack>
  )
}

const PregnancyArchiveHistory = ({ assessment }: { assessment: PopulatedAssessment | null }) => {
  // const { archivedHistory } = assessment ?? {}
  const { appName } = useApp()
  const archivedHistory = useMemo(() => {
    if (appName === 'app') return assessment?.adminArchivedHistory
    return assessment?.practiceArchivedHistory
  }, [appName, assessment])
  const reversedArchivedHistory = useMemo(
    () => [...(archivedHistory ?? [])].toReversed(),
    [archivedHistory],
  )
  return (
    <VStack spacing={0} divider={<Divider />}>
      {reversedArchivedHistory.length ? (
        reversedArchivedHistory.map(h => <PregnancyArchiveHistoryItem key={`${h.on}`} item={h} />)
      ) : (
        <Text p={2} color="gray.600" fontStyle="italic">
          No history to show
        </Text>
      )}
    </VStack>
  )
}

const PregnancyArchiveHistoryPopover = ({
  assessment,
}: {
  assessment: PopulatedAssessment | null
}) => (
  <Popover placement="left" strategy="fixed" trigger="hover">
    <PopoverTrigger>
      <IconButton
        ml="auto"
        aria-label="History"
        gap={1}
        bg="transparent"
        color="red.700"
        size="xs"
        icon={<RepeatClockIcon w={4} h={4} />}
        borderRadius="full"
      />
    </PopoverTrigger>
    <PopoverContent>
      <PopoverBody maxH="350px" overflowY="auto" p={0}>
        <PregnancyArchiveHistory assessment={assessment} />
      </PopoverBody>
      <PopoverArrow />
    </PopoverContent>
  </Popover>
)

const PregnancyArchiveButton = ({
  children,
  loadingArchived,
  width,
  readOnly,
  setHovered,
  setConfirmArchive,
}: PropsWithChildren<{
  loadingArchived?: boolean
  width?: string
  readOnly?: boolean
  setHovered: (hovered: boolean) => void
  setConfirmArchive: (confirmArchive: boolean) => void
}>) => (
  <Button
    borderRadius="full"
    color="red.600"
    h={7}
    bg="gray.50"
    isLoading={loadingArchived}
    w={width}
    transition="width 300ms"
    border="1px solid"
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    pointerEvents={readOnly ? 'none' : 'auto'}
    borderColor="red.600"
    fontWeight={500}
    size="sm"
    onClick={() => setConfirmArchive(true)}>
    {children}
  </Button>
)

const PregnancyArchiveBadge = ({ archivedOn }: { archivedOn: number | null | undefined }) => (
  <Badge
    bg={archivedOn ? 'red.200' : 'transparent'}
    color={archivedOn ? 'red.800' : 'red.500'}
    borderColor="red.600"
    borderWidth={archivedOn ? 0 : 1}
    borderRadius="full"
    px={2.5}
    fontSize="sm"
    fontWeight={500}>
    {archivedOn ? `ARCHIVED ${getDateString(archivedOn, 'short')}` : 'NOT ARCHIVED'}
  </Badge>
)

export const PregnancyArchiveStatus = ({
  readOnly,
  practiceStatus,
}: {
  readOnly?: boolean
  practiceStatus?: boolean
}) => {
  const { confirmArchive, setConfirmArchive, handleArchive, loadingArchived, archivedOn } =
    useAssessmentArchived(practiceStatus)
  const {
    selectedAssessment: { populated: assessment },
  } = useProfile()

  const [hovered, setHovered] = useState(false)
  const { text, width } = useMemo(() => {
    if (!archivedOn) return { text: 'ARCHIVE', width: '80px' }
    if (hovered) return { text: 'UNARCHIVE', width: '170px' }
    return { text: `ARCHIVED ${getDateString(archivedOn, 'short')}`, width: '170px' }
  }, [archivedOn, hovered])

  return (
    <Flex w="100%" px={3} py={1} bg="#efefef">
      <Flex w="100%" align="center" justify="space-between">
        {readOnly ? (
          <PregnancyArchiveBadge archivedOn={archivedOn} />
        ) : (
          <PregnancyArchiveButton
            loadingArchived={loadingArchived}
            width={width}
            readOnly={readOnly}
            setHovered={setHovered}
            setConfirmArchive={setConfirmArchive}>
            {text}
          </PregnancyArchiveButton>
        )}
        <PregnancyArchiveHistoryPopover assessment={assessment} />
        {readOnly ? null : (
          <ConfirmArchiveAlert
            confirmActive={confirmArchive}
            onCancel={() => setConfirmArchive(false)}
            onConfirm={() => {
              handleArchive()
              setConfirmArchive(false)
            }}
            actionLoading={loadingArchived}
            isArchived={!!archivedOn}
          />
        )}
      </Flex>
    </Flex>
  )
}
